import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import TimeInput from "@/components/TimeInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";

export const BookingDialog = ({
  open, onOpenChange,
  bookingForm, setBookingForm,
  bookingTotal,
  customerSuggestions, showSuggestions, setShowSuggestions,
  selectedCustomerId, setSelectedCustomerId,
  searchCustomers, selectCustomer,
  resources,
  toggleResource, updateResourceQuantity,
  recurringPreview, setRecurringPreview, recurringLoading,
  previewRecurring,
  createBooking, createRecurringBookings,
  selectedDate,
}) => {
  const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const startWeekday = selectedDate
    ? (new Date(selectedDate).getDay() + 6) % 7   // JS 0=Sun..6=Sat → ISO 0=Mon..6=Sun
    : 0;
  const toggleRecurrenceWeekday = (wd) => {
    setBookingForm(prev => {
      const cur = new Set(prev.repeat_weekdays || []);
      if (cur.has(wd)) cur.delete(wd); else cur.add(wd);
      // Don't allow zero weekdays — keep at least start_date's weekday
      if (cur.size === 0) cur.add(startWeekday);
      return { ...prev, repeat_weekdays: [...cur].sort() };
    });
    setRecurringPreview(null);
  };
  const handleRecurringToggle = (checked) => {
    setBookingForm(prev => ({
      ...prev,
      is_recurring: checked,
      // Pre-seed start_date's weekday on first enable
      repeat_weekdays: checked && (!prev.repeat_weekdays || prev.repeat_weekdays.length === 0)
        ? [startWeekday]
        : prev.repeat_weekdays,
    }));
    setRecurringPreview(null);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">New Room Booking</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Customer Info with Auto-Suggest */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1 relative">
              <Label className="text-xs">Booked By *</Label>
              <Input
                data-testid="renter-name"
                value={bookingForm.renter_name}
                onChange={(e) => {
                  const val = e.target.value;
                  setBookingForm({...bookingForm, renter_name: val});
                  setSelectedCustomerId(null);
                  searchCustomers(val);
                }}
                onFocus={() => { if (customerSuggestions.length > 0) setShowSuggestions(true); }}
                placeholder="Type name to search..."
                className="h-10 rounded-xl text-sm"
                autoComplete="off"
              />
              {showSuggestions && customerSuggestions.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {customerSuggestions.map((c) => (
                    <button
                      key={c.customer_id}
                      onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm border-b border-slate-50 last:border-0"
                      data-testid={`customer-suggestion-${c.customer_id}`}
                    >
                      <span className="font-medium text-slate-900">{c.name}</span>
                      <span className="text-xs text-slate-500 ml-2">{c.phone}</span>
                      {c.company_name && <span className="text-xs text-blue-500 ml-2">{c.company_name}</span>}
                    </button>
                  ))}
                </div>
              )}
              {selectedCustomerId && (
                <span className="text-[10px] text-green-600 mt-0.5 block">Linked to existing customer</span>
              )}
            </div>
            <div className="space-y-1 relative">
              <Label className="text-xs">Phone *</Label>
              <Input
                data-testid="renter-phone"
                value={bookingForm.renter_phone}
                onChange={(e) => {
                  const val = e.target.value;
                  setBookingForm({...bookingForm, renter_phone: val});
                  if (!selectedCustomerId) searchCustomers(val);
                }}
                placeholder="Mobile number"
                className="h-10 rounded-xl text-sm"
                autoComplete="off"
              />
              {!selectedCustomerId && bookingForm.renter_phone.length >= 2 && showSuggestions && customerSuggestions.length > 0 && !bookingForm.renter_name && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {customerSuggestions.map((c) => (
                    <button
                      key={c.customer_id}
                      onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm border-b border-slate-50 last:border-0"
                    >
                      <span className="font-medium text-slate-900">{c.name}</span>
                      <span className="text-xs text-slate-500 ml-2">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Email</Label>
            <Input value={bookingForm.renter_email} onChange={(e) => setBookingForm({...bookingForm, renter_email: e.target.value})} placeholder="Optional" className="h-10 rounded-xl text-sm" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Start *</Label>
              <TimeInput data-testid="start-time-input" value={bookingForm.start_time} onChange={(e) => setBookingForm({...bookingForm, start_time: e.target.value})} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End *</Label>
              <TimeInput data-testid="end-time-input" value={bookingForm.end_time} onChange={(e) => setBookingForm({...bookingForm, end_time: e.target.value})} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{bookingForm.is_recurring ? "Total Rent for Series (₹) *" : "Rent (₹) *"}</Label>
              <Input data-testid="rent-input" type="number" value={bookingForm.agreed_rent} onChange={(e) => setBookingForm({...bookingForm, agreed_rent: e.target.value})} className="h-10 rounded-xl text-sm px-2" />
            </div>
          </div>
          {bookingForm.is_recurring && (
            <p className="text-[10px] text-blue-600 -mt-2">This amount applies to the ENTIRE recurring series, not per slot.</p>
          )}

          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Discount</Label>
              <Input type="number" value={bookingForm.discount} onChange={(e) => setBookingForm({...bookingForm, discount: e.target.value})} className="h-10 rounded-xl text-sm" />
            </div>
            <select value={bookingForm.discount_type} onChange={(e) => setBookingForm({...bookingForm, discount_type: e.target.value})} className="h-10 rounded-xl border px-2 text-sm w-20 bg-white">
              <option value="flat">₹</option>
              <option value="percent">%</option>
            </select>
            <div className="h-10 px-3 rounded-xl bg-slate-100 flex items-center">
              <span className="text-sm font-medium text-slate-700">Total: ₹{bookingTotal}</span>
            </div>
          </div>

          <div className="space-y-2 p-3 bg-slate-50 rounded-xl">
            <Label className="text-xs font-semibold">Payment</Label>
            <select value={bookingForm.payment_status} onChange={(e) => setBookingForm({...bookingForm, payment_status: e.target.value})} className="w-full h-10 rounded-xl border px-3 bg-white text-sm">
              <option value="pending">Unpaid</option>
              <option value="partial">Partial Payment</option>
              <option value="paid">Fully Paid</option>
            </select>
            {bookingForm.payment_status !== "pending" && (
              <div className="space-y-2 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Amount Paid *</Label>
                    <Input type="number" value={bookingForm.payment_status === "paid" ? bookingTotal : bookingForm.amount_paid} disabled={bookingForm.payment_status === "paid"} onChange={(e) => setBookingForm({...bookingForm, amount_paid: e.target.value})} className="h-10 rounded-xl text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Method</Label>
                    <select value={bookingForm.payment_method} onChange={(e) => setBookingForm({...bookingForm, payment_method: e.target.value})} className="w-full h-10 rounded-xl border px-2 bg-white text-sm">
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Payment Date</Label>
                    <Input type="date" value={bookingForm.payment_date} onChange={(e) => setBookingForm({...bookingForm, payment_date: e.target.value})} className="h-10 rounded-xl text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Reference #</Label>
                    <Input value={bookingForm.reference_number} onChange={(e) => setBookingForm({...bookingForm, reference_number: e.target.value})} placeholder="Optional" className="h-10 rounded-xl text-sm" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea value={bookingForm.notes} onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})} className="rounded-xl text-sm" rows={2} />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Calendar Color (optional)</Label>
            <div className="flex items-center gap-3">
              <input
                data-testid="booking-color-input"
                type="color"
                value={bookingForm.color || "#7C3AED"}
                onChange={(e) => setBookingForm({...bookingForm, color: e.target.value})}
                className="h-9 w-12 cursor-pointer rounded-md border border-slate-300"
              />
              <span className="text-xs text-slate-500">
                {bookingForm.color
                  ? <>Selected: <code className="text-slate-700">{bookingForm.color}</code></>
                  : "Uses payment-status color by default"}
              </span>
              {bookingForm.color && (
                <button
                  type="button"
                  onClick={() => setBookingForm({...bookingForm, color: ""})}
                  className="text-xs text-violet-600 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {resources.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Resources Required</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                {resources.map(resource => {
                  const selected = bookingForm.resources_required.find(r => r.resource_id === resource.resource_id);
                  return (
                    <div key={resource.resource_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={!!selected}
                        onChange={() => toggleResource(resource.resource_id)}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm flex-1 truncate">{resource.name} <span className="text-xs text-slate-400">(avail: {resource.quantity})</span></span>
                      {selected && (
                        <Input
                          type="number"
                          min="1"
                          max={resource.quantity}
                          value={selected.quantity}
                          onChange={(e) => updateResourceQuantity(resource.resource_id, e.target.value)}
                          className="w-16 h-8 text-sm rounded-lg"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="p-3 bg-blue-50 rounded-xl space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="booking-recurring-toggle" checked={bookingForm.is_recurring} onChange={(e) => handleRecurringToggle(e.target.checked)} className="rounded" />
              <span className="text-sm font-medium text-blue-800">Recurring Booking</span>
            </label>
            {bookingForm.is_recurring && (
              <div className="space-y-3 mt-2">
                {/* Frequency selector — weekly = multi-day, others = legacy single-day */}
                <div className="space-y-1">
                  <Label className="text-xs">Recurrence Type</Label>
                  <select
                    data-testid="booking-recurrence-type"
                    value={bookingForm.recurrence_type}
                    onChange={(e) => { setBookingForm({...bookingForm, recurrence_type: e.target.value}); setRecurringPreview(null); }}
                    className="w-full h-10 rounded-xl border px-2 bg-white text-sm"
                  >
                    <option value="weekly">Weekly (one or more weekdays)</option>
                    <option value="monthly">Monthly (same day each month)</option>
                  </select>
                </div>

                {bookingForm.recurrence_type === "weekly" ? (
                  <>
                    {/* Repeat Every N week(s) */}
                    <div className="space-y-1">
                      <Label className="text-xs">Repeat Every</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid="booking-repeat-interval"
                          type="number" min="1" max="12"
                          value={bookingForm.repeat_interval}
                          onChange={(e) => { setBookingForm({...bookingForm, repeat_interval: e.target.value}); setRecurringPreview(null); }}
                          className="h-10 rounded-xl text-sm w-24"
                        />
                        <span className="text-sm text-slate-600">week(s)</span>
                      </div>
                    </div>

                    {/* Repeat On — multi-day chips */}
                    <div className="space-y-1">
                      <Label className="text-xs">Repeat On</Label>
                      <div className="flex flex-wrap gap-1.5" data-testid="booking-weekday-chips">
                        {WEEKDAY_LABELS.map((label, idx) => {
                          const active = (bookingForm.repeat_weekdays || []).includes(idx);
                          return (
                            <button
                              key={label}
                              type="button"
                              data-testid={`booking-weekday-${label.toLowerCase()}`}
                              onClick={() => toggleRecurrenceWeekday(idx)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                active
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      {(bookingForm.repeat_weekdays || []).length === 0 && (
                        <p className="text-[10px] text-orange-600">Select at least one weekday.</p>
                      )}
                    </div>

                    {/* Ends */}
                    <div className="space-y-1">
                      <Label className="text-xs">Ends</Label>
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="radio"
                            data-testid="booking-ends-after-weeks"
                            checked={bookingForm.ends_kind === "after_weeks"}
                            onChange={() => { setBookingForm({...bookingForm, ends_kind: "after_weeks"}); setRecurringPreview(null); }}
                          />
                          <span>After</span>
                          <Input
                            type="number" min="1" max="52"
                            value={bookingForm.ends_weeks}
                            onChange={(e) => { setBookingForm({...bookingForm, ends_weeks: e.target.value}); setRecurringPreview(null); }}
                            disabled={bookingForm.ends_kind !== "after_weeks"}
                            className="h-8 w-20 rounded-lg text-sm"
                          />
                          <span>week(s)</span>
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="radio"
                            data-testid="booking-ends-on-date"
                            checked={bookingForm.ends_kind === "on_date"}
                            onChange={() => { setBookingForm({...bookingForm, ends_kind: "on_date"}); setRecurringPreview(null); }}
                          />
                          <span>On</span>
                          <Input
                            type="date"
                            value={bookingForm.ends_date}
                            onChange={(e) => { setBookingForm({...bookingForm, ends_date: e.target.value}); setRecurringPreview(null); }}
                            disabled={bookingForm.ends_kind !== "on_date"}
                            className="h-8 rounded-lg text-sm"
                          />
                        </label>
                      </div>
                    </div>
                  </>
                ) : (
                  // Legacy monthly path — unchanged single-day-of-month behaviour
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">End by</Label>
                      <select value={bookingForm.recurrence_end_type} onChange={(e) => setBookingForm({...bookingForm, recurrence_end_type: e.target.value})} className="w-full h-10 rounded-xl border px-2 bg-white text-sm">
                        <option value="occurrences"># of occurrences</option>
                        <option value="end_date">End date</option>
                      </select>
                    </div>
                    {bookingForm.recurrence_end_type === "occurrences" ? (
                      <div className="space-y-1">
                        <Label className="text-xs">Occurrences</Label>
                        <Input type="number" min="1" value={bookingForm.num_occurrences} onChange={(e) => setBookingForm({...bookingForm, num_occurrences: e.target.value})} className="h-10 rounded-xl text-sm" />
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Label className="text-xs">End Date</Label>
                        <Input type="date" value={bookingForm.recurrence_end_date} onChange={(e) => setBookingForm({...bookingForm, recurrence_end_date: e.target.value})} className="h-10 rounded-xl text-sm" />
                      </div>
                    )}
                  </div>
                )}

                <Button data-testid="booking-preview-recurring-btn" onClick={previewRecurring} disabled={recurringLoading} variant="outline" size="sm" className="rounded-full text-xs w-full">
                  {recurringLoading ? "Checking..." : "Preview Dates & Check Conflicts"}
                </Button>

                {recurringPreview && (
                  <div className="mt-2 p-2 bg-white rounded-lg max-h-60 overflow-y-auto space-y-2" data-testid="booking-recurring-preview">
                    <p className="text-xs font-semibold text-slate-700">
                      {recurringPreview.total} bookings · {recurringPreview.conflicts_count} conflicts
                    </p>
                    {(() => {
                      // Group by weekday for the multi-day view; fall back to flat list for legacy single-day
                      const grouped = {};
                      recurringPreview.dates.forEach(d => {
                        const wd = typeof d.weekday === "number" ? d.weekday : null;
                        const key = wd !== null ? wd : "_flat";
                        if (!grouped[key]) grouped[key] = [];
                        grouped[key].push(d);
                      });
                      const order = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));
                      if (order.length === 1 && order[0] === "_flat") {
                        // Legacy flat list
                        return recurringPreview.dates.map((d, i) => (
                          <div key={`${d.date}-${i}`} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${d.has_conflict ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
                            <span>{d.has_conflict ? "✕" : "✓"}</span>
                            <span>{format(new Date(d.date), 'EEE, dd MMM yyyy')}</span>
                            {d.has_conflict && <span className="text-[10px]">- Conflict</span>}
                          </div>
                        ));
                      }
                      return order.map(key => {
                        const rows = grouped[key];
                        const conflictsInGroup = rows.filter(r => r.has_conflict).length;
                        return (
                          <div key={key} className="space-y-1" data-testid={`booking-preview-group-${key}`}>
                            <div className="flex items-center justify-between bg-slate-100 rounded px-2 py-1">
                              <span className="text-xs font-semibold text-slate-800">
                                {WEEKDAY_LABELS[Number(key)]} • {rows.length} bookings
                              </span>
                              {conflictsInGroup > 0 && (
                                <span className="text-[10px] text-red-700">{conflictsInGroup} conflict{conflictsInGroup > 1 ? "s" : ""}</span>
                              )}
                            </div>
                            {rows.map((d, i) => (
                              <div key={`${d.date}-${i}`} className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${d.has_conflict ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
                                <span>{d.has_conflict ? "✕" : "✓"}</span>
                                <span>{format(new Date(d.date), 'EEE, dd MMM yyyy')}</span>
                                {d.has_conflict && <span className="text-[10px]">- conflict with existing booking</span>}
                              </div>
                            ))}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          {bookingForm.is_recurring ? (
            <Button data-testid="create-recurring-btn" onClick={createRecurringBookings} disabled={!recurringPreview || recurringPreview.dates.filter(d => !d.has_conflict).length === 0} className="w-full rounded-xl h-12 bg-green-600 hover:bg-green-700">
              Book {recurringPreview ? recurringPreview.dates.filter(d => !d.has_conflict).length : 0} Dates
            </Button>
          ) : (
            <Button data-testid="confirm-booking-btn" onClick={createBooking} className="w-full rounded-xl h-12 bg-green-600 hover:bg-green-700">
              Confirm Booking
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
